import { CountryCode } from '@checkout/countryCodes';
import { ILocation, RootState } from '@checkout/state';
import { FormikProps } from 'formik';
import { AsYouType, PhoneNumber, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import { ChangeEvent, FocusEvent, KeyboardEvent, createRef } from 'react';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { IntlTelephoneSelect, TextInput } from './../../presentational';
import CountriesList from './CountriesList';

interface IOwnProps {
  initialCountry: CountryCode;
  id: string;
  type: 'text' | 'password' | 'hidden' | 'tel' | 'email' | 'number';
  label?: string;
  name: string;
  sizeVariant?: 'large' | 'medium' | 'half' | 'small';
  variant?: 'simple';
  value: any;
  form: FormikProps<any>;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  errors?: {};
  touched?: any;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  submitOnEnter?: boolean;
  successMessage?: string;
  hint?: string;
  placeholder?: string;
  readOnly?: boolean;
}

interface IConnectedProps {
  location: ILocation;
}

interface ICountryCallingCode extends String { }

interface IOwnState {
  countryCollingCode: ICountryCallingCode;
  countryCode: CountryCode;
  value: string;
}

type Props = IOwnProps & IConnectedProps & InjectedIntlProps;

class IntlTelephoneInput extends React.Component<Props, IOwnState> {

  private telRef = createRef<HTMLInputElement>();

  getCountryDialCodeByCountryCode = (countryCode: CountryCode) => {
    let countryCallingCode = '' as ICountryCallingCode;

    try {
      countryCallingCode = getCountryCallingCode(countryCode);
    } catch (error) {
      return countryCallingCode;
    }
    return `+${countryCallingCode}`;
  }

  getCountryCodeByPhoneNumber = (phoneNumber: string) => {
    let countryCode = this.props.initialCountry;
    const formatter = new AsYouType();
    formatter.input(phoneNumber);

    if (phoneNumber && formatter.country) {
      countryCode = formatter.country;
    } else if (this.state && this.state.countryCode) {
      countryCode = this.state.countryCode;
    }

    return countryCode;
  }

  getForrmatedPhoneValue = (phoneNumber: string) => {
    const countryCode = this.state && this.state.countryCode || this.props.initialCountry;
    const phone = parsePhoneNumberFromString(phoneNumber, countryCode);
    let forrmatedValue = new AsYouType(countryCode).input(phoneNumber);

    if (phone) {
      forrmatedValue = new AsYouType(countryCode).input(phone.number.toString());
    }

    return forrmatedValue;
  }

  state: IOwnState = {
    // countryCollingCode: this.getCountryDialCodeByCountryCode(this.props.initialCountry),
    countryCollingCode: this.getCountryDialCodeByCountryCode(this.getCountryCodeByPhoneNumber(this.props.value)),
    // countryCode: this.props.initialCountry,
    countryCode: this.getCountryCodeByPhoneNumber(this.props.value),
    value: this.props.value,
    /*
    TODO:
      Think if reasonable is auto format value on component load or maybe show error and let the user correct the phone manually
      If you want to turn on auto formatting on component load, please go ahead and uncomment below line
    */
    // value: this.getForrmatedPhoneValue(this.props.value),
  };

  prefillCorrectedPhoneMumber = (phone: PhoneNumber | undefined, countryCollingCode: ICountryCallingCode, shouldPrefillCountryCollingCode = true) => {
    if (phone) {
      this.setCorrectedPhoneMumber(phone.nationalNumber.toString());
    } else if (shouldPrefillCountryCollingCode) {
      this.prefillCountryCollingCode(countryCollingCode);
    }
  }

  componentDidUpdate(nextProps: Props) {
    const countryCollingCode = this.getCountryDialCodeByCountryCode(this.props.initialCountry);

    if (!isEqual(this.props.initialCountry, nextProps.initialCountry)) {
      const phone = parsePhoneNumberFromString(this.state.value, this.props.initialCountry);

      this.setState({ countryCode: this.props.initialCountry, countryCollingCode }, () => {
        this.prefillCorrectedPhoneMumber(phone, countryCollingCode, this.state.value !== '');
      });
    }
  }

  prefillCountryCollingCode = (countryCollingCode: ICountryCallingCode) => {
    this.setState({ value: `${countryCollingCode} ` }, () => {
      this.props.form.setFieldValue(this.props.name, this.state.value);
    });
  }

  focus = () => {
    const node = this.telRef.current;
    if (node) {
      node.focus();
    }
  }

  handleCountrySelectChange = (event: ChangeEvent<any>) => {
    const { value: countryCode } = event.target;
    const countryCollingCode = this.getCountryDialCodeByCountryCode(countryCode);
    const phone = parsePhoneNumberFromString(this.state.value, countryCode);

    this.setState({ countryCode, countryCollingCode }, () => {
      this.prefillCorrectedPhoneMumber(phone, countryCollingCode);
      this.focus();
    });
  }

  setCorrectedPhoneMumber = (phoneNumber: string) => {
    const forrmatedValue = this.getForrmatedPhoneValue(phoneNumber);

    this.setState({ value: forrmatedValue }, () => {
      this.props.form.setFieldValue(this.props.name, this.state.value);
    });
  }

  handleBlur = (event: FocusEvent<any>) => {
    const { value } = event.target;

    this.setCorrectedPhoneMumber(value);

    // Need execute onBlur from props to fire formik validation field
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  }

  handlePaste = (event: React.ClipboardEvent<any>) => {
    const pastedPnoneNumber = event.clipboardData.getData('Text');
    const countryCode = this.getCountryCodeByPhoneNumber(pastedPnoneNumber);
    const countryCollingCode = this.getCountryDialCodeByCountryCode(countryCode);

    this.setState({ countryCollingCode, countryCode });
  }

  handleFocus = () => {
    // Prefill country colling code (dial code) only when input is empty
    if (!this.state.value) {
      this.prefillCountryCollingCode(this.state.countryCollingCode);
    }
  }

  handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    const { value } = event.target;
    const countryCode = this.getCountryCodeByPhoneNumber(value);
    const countryCollingCode = this.getCountryDialCodeByCountryCode(this.state.countryCode);

    this.setState({ value, countryCollingCode, countryCode }, () => {
      if (this.props.onChange) {
        this.props.onChange(event);
      }
    });
  }

  render() {
    const { id, name, placeholder, label, onBlur, onFocus, initialCountry: _, intl: ___, location: ____, ...rest } = this.props;
    const { value, countryCode } = this.state;

    const sortedCountriesList = CountriesList.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));

    const intlTelephoneSelectComponent = (
      <IntlTelephoneSelect
        countriesList={sortedCountriesList}
        handleSelectChange={this.handleCountrySelectChange}
        countryCode={countryCode}
      />
    );

    return (
      <TextInput
        ref={this.telRef}
        id={id}
        name={name}
        type="text"
        placeholder={placeholder}
        label={label}
        {...rest}
        value={value}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
        onChange={this.handleChange}
        onPaste={this.handlePaste}
        intlTelephoneSelectComponent={intlTelephoneSelectComponent}
      />
    );
  }
}

export default compose<React.ComponentClass<IOwnProps>>(
  injectIntl,
  connect<IConnectedProps, {}>(
    (state: RootState) => ({
      location: state.checkout.location,
    }),
    {}
  ),
)(IntlTelephoneInput);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/presentational/IntlTelephoneInput/IntlTelephoneInput.tsx